import { FilterValue, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '../../../tables/WorkerTable/WorkerTable.styles';
import { EditableCell } from '@app/components/tables/editableTable/EditableCell';
import { FilterIcon } from '../../icons/FilterIcon';
import { categoryColumns } from './Column';
import { Button } from '../../buttons/Button/Button';
import { notificationController } from '@app/controllers/notificationController';
import { BasicTableRow, getSortBy, QueryTable } from '@app/api/table.api';
import { Space } from 'antd';
import { Popconfirm } from '../../Popconfirm/Popconfirm';
import { EditIcon } from '../../icons/EditIcon';
import { DeleteIcon } from '../../icons/DeleteIcon';
import { AddCategory } from '../AddCategory/AddCategory';
import { IDealRoomCategory } from '@app/interfaces/deal-room-category';
import { deleteCategory, getCategoryListWithPagination } from '@app/api/deal-room-category';
import { useDialog } from '@app/hooks/useDialog';
import { useLoader } from '@app/hooks/useLoader';
import { TableRecordType } from '@app/types/generalTypes';
import { Spinner } from '../../Spinner/Spinner';

export const useTableData = (
  initialPagination: QueryTable,
): {
  tableData: { data: IDealRoomCategory[]; pagination: QueryTable; loading: boolean };
  setTableData: React.Dispatch<
    React.SetStateAction<{ data: IDealRoomCategory[]; pagination: QueryTable; loading: boolean }>
  >;
  fetch: (pagination: QueryTable) => void;
} => {
  const [tableData, setTableData] = useState<{ data: IDealRoomCategory[]; pagination: QueryTable; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const fetch = useCallback((pagination: QueryTable) => {
    setTableData((tableData) => ({ ...tableData, loading: true }));
    getCategoryListWithPagination(pagination)
      .then((res) => {
        setTableData((tableData) => ({
          ...tableData,
          data: res.results.map((item: IDealRoomCategory) => {
            item.key = item.id;
            return item;
          }),
          pagination: {
            page: res.page,
            limit: res.limit,
            total: res.totalResults,
            search: pagination.search,
            sortBy: pagination.sortBy,
          },
          loading: false,
        }));
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
      });
  }, []);

  return { tableData, setTableData, fetch };
};

export interface CategoryProps {
  dealRoomId: string;
}

export const Category: React.FC<CategoryProps> = ({ dealRoomId }) => {
  const { t } = useTranslation();
  const [initialPagination, setInitialPagination] = useState<QueryTable>({
    page: 1,
    limit: 10,
    search: '',
    sortBy: '_id:desc',
  });
  const [query, setQuery] = useState('');

  const { dialogOpen, handleDialogOpen, handleDialogClose } = useDialog();
  const { loader, handleLoaderOpen, handleLoaderClose } = useLoader();
  const { tableData, fetch } = useTableData(initialPagination);
  const [selectedCategory, setSelectedCategory] = useState<IDealRoomCategory | null>(null);

  const onDialogClose = () => {
    handleDialogClose();
    setSelectedCategory(null);
  };

  const onEdit = (record: IDealRoomCategory) => {
    setSelectedCategory(record);
    handleDialogOpen();
  };

  const onDelete = (id: string) => {
    handleLoaderOpen();
    deleteCategory(id)
      .then(() => {
        notificationController.success({ message: t('dealRooms.selectMenu.deleteCategorySuccess') });
        fetch(initialPagination);
        handleLoaderClose();
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
      })
      .finally(() => {
        handleLoaderClose();
      });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<TableRecordType> | SorterResult<TableRecordType>[],
  ) => {
    const sortBy = Array.isArray(sorter) ? getSortBy(sorter[0]) : getSortBy(sorter);
    const newPagination = {
      page: pagination.current || 1,
      limit: pagination.pageSize || 10,
      search: query,
      sortBy: sortBy ? sortBy : '_id:desc',
    };
    setInitialPagination(newPagination);
    fetch(newPagination);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPagination = {
      ...initialPagination,
      search: event.target.value,
    };
    setQuery(event.target.value);
    setInitialPagination(newPagination);
    fetch(newPagination);
  };

  const columns = [
    ...categoryColumns?.map((item) => {
      item.title = t(item.title);
      return item;
    }),
    {
      title: t('dealRooms.selectMenu.actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center' as const,
      render: (_text: string, record: BasicTableRow) => {
        return (
          <>
            <Space>
              <S.ActionButton title={t('common.edit')} onClick={() => onEdit(record as unknown as IDealRoomCategory)}>
                <EditIcon />
              </S.ActionButton>
              <Popconfirm
                title={t('dealRooms.selectMenu.deleteCategoryInfo')}
                okText={t('common.yes')}
                cancelText={t('common.no')}
                onConfirm={() => onDelete((record as unknown as IDealRoomCategory)?.id || '')}
              >
                <S.ActionButton title={t('common.delete')}>
                  <DeleteIcon />
                </S.ActionButton>
              </Popconfirm>
            </Space>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch, initialPagination]);

  return (
    <>
      {loader && <Spinner />}
      {dialogOpen && (
        <AddCategory
          open={dialogOpen}
          dealRoomId={dealRoomId}
          dealRoomCategory={selectedCategory}
          onCancel={onDialogClose}
          onSuccess={() => {
            fetch(initialPagination);
            onDialogClose();
          }}
        />
      )}
      <S.TableActionWrapper className="width-100 between">
        <S.InputSearch
          placeholder={t('header.search')}
          className="search-input"
          onChange={(event) => handleSearch(event)}
          value={query}
          filter={!query ? <S.Btn size="small" type={'text'} aria-label="Filter" icon={<FilterIcon />} /> : null}
          allowClear
          enterButton={null}
          addonAfter={null}
        />
        <S.FlexWrapper>
          <Button type="primary" onClick={() => handleDialogOpen()}>
            {t('dealRooms.selectMenu.addCategory')}
          </Button>
        </S.FlexWrapper>
      </S.TableActionWrapper>
      <S.ScrollTable
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        bordered
        dataSource={tableData.data}
        columns={columns}
        rowClassName="editable-row"
        pagination={{
          current: tableData.pagination.page,
          pageSize: tableData.pagination.limit,
          total: tableData.pagination.total,
          locale: { items_per_page: `/ ${t('common.page')}` },
        }}
        onChange={handleTableChange}
        loading={tableData.loading}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('common.noData') }}
      />
    </>
  );
};
